import { AxiosError, AxiosResponse } from "axios";
import { IResponse, IServerError } from "@websential/cosmic-v20";

import { IDropDownListResponse } from "../models/common/IDropDownListResponse";
import { IGetListRequest } from "../models";
import { ISalaryProfileListModel } from "../models";
import { ISalaryProfileRequestPayload } from "../models";
import http from "../common/http-commons";

export const SalaryProfileService = {
  getSalaryProfileById: async (
    id: string | undefined,
    clientId: string,
    taxYear: number
  ): Promise<any | IServerError | IResponse | null> => {
    if (id) {
      try {
        const response: AxiosResponse<any> = await http.get<any>(
          `/v1/salary-profile/${id}/`
        );

        if (response.status === 200) {
          return response.data;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }

        return {
          status: false,
          message: "Something went wrong please try again later.",
        };
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("An error occurred:", axiosError);
        const errorMessage =
          axiosError.response && typeof axiosError.response.data === "string"
            ? axiosError.response.data
            : "Unknown error";
        return {
          status: false,
          message: errorMessage,
        };
      }
    }

    return null;
  },
  getResourceSalaryProfileDropDownList: async (
    resourceId: string,
    clientId: string,
    taxYear: number
  ): Promise<IDropDownListResponse[] | IServerError | IResponse> => {
    const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
      IDropDownListResponse[]
    >(
      `/v1/salary-profile/ddl-salary-profile/${resourceId}?client_id=${clientId}&tax_year=${taxYear}`
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  delSalaryProfile: async (
    requestPayload: any
  ): Promise<string | IServerError | IResponse> => {
    const response: AxiosResponse<string> = await http.delete<string>(
      `/v1/t4-slips/${requestPayload.salaryProfileId}/?client_id=${requestPayload.clientId}&tax_year=${requestPayload.taxYear}`
    );

    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    } else if (response.status === 400) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  delBulkSlips: async (
    requestPayload: any
  ): Promise<string | IServerError | IResponse> => {
    const response = await http.delete<string>(
      `/v1/t4-slips/bulk-delete/${requestPayload.clientId}/?tax_year=${requestPayload.taxYear}`,
      { data: requestPayload.ids }
    );
    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getList: async (
    clientId: string,
    taxYear: number,
    params: IGetListRequest
  ): Promise<ISalaryProfileListModel[] | IServerError | IResponse> => {
    const response: AxiosResponse<ISalaryProfileListModel[]> = await http.get<
      ISalaryProfileListModel[]
    >(
      `/v1/t4-slips/?client_id=${clientId}&tax_year=${taxYear}&order_by=${params.order_by}&order_direction=${params.order_direction}&page=${params.page}&size=${params.size}&search_query=${params.search_query}`
    );

    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  create: async (
    requestPayload: ISalaryProfileRequestPayload
  ): Promise<ISalaryProfileRequestPayload | IServerError | IResponse | any> => {
    if (requestPayload.id) {
      const response: AxiosResponse<ISalaryProfileRequestPayload> =
        await http.put<ISalaryProfileRequestPayload>(
          `/v1/salary-profile/${requestPayload.id}/`,
          requestPayload
        );

      if (response.status === 200) {
        return { status: response.status, data: response.data };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
    }

    const response: AxiosResponse<ISalaryProfileRequestPayload> =
      await http.post<ISalaryProfileRequestPayload>(
        `/v1/salary-profile/?client_id=${requestPayload.clientId}`,
        requestPayload
      );

    if (response.status === 201) {
      return { status: response.status, data: response.data };
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    } else if (response.status === 400) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getEmployeeDDL: async (
    clientId: string,
    taxYear?: number
  ): Promise<any | IServerError | IResponse | null> => {
    const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
      IDropDownListResponse[]
    >(`/v1/employees/ddl?client_id=${clientId}`);

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getEmployeeDDLByStatus: async (
    clientId: string,
    status: number,
    taxYear?: number
  ): Promise<any | IServerError | IResponse | null> => {
    const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
      IDropDownListResponse[]
    >(
      `/v1/employees-status/employee-status-ddl?client_id=${clientId}&emplooyee_status=${status}`
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getSalaryProfileDDL: async (
    // clientId: string,
    employeeId: string,
    // taxYear?: number
  ): Promise<any | IServerError | IResponse | null> => {
    if (employeeId) {
      const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
        IDropDownListResponse[]
      >(`/v1/salary-profile/salary-profile-ddl?employee_id=${employeeId}`);

      if (response.status === 200) {
        return response.data;
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } else {
      return null;
    }
  },
  getProfileAppliesFromPayPeriodDDL: async (
    id: string | undefined,
    clientId: string,
    employeePayPeriodId: string,
    taxYear?: number
  ): Promise<any | IServerError | IResponse | null> => {
    if (employeePayPeriodId) {
      try {
        const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
          IDropDownListResponse[]
        >(
          `/v1/payroll-schedule/ddl/pay_period_dates?client_id=${clientId}&payroll_schedule_id=${employeePayPeriodId}&tax_year=${taxYear}`
        );

        if (response.status === 200) {
          return response.data;
        }

        return {
          status: false,
          message: "Something went wrong please try again later.",
        };
      } catch {}
    }
    return null;
  },
  getEmployeeInformationById: async (
    resourceId: string | undefined
  ): Promise<any | IServerError | IResponse | null> => {
    if (resourceId) {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/employees/${resourceId}/`
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    }

    return null;
  },
  getEDREarningsAndTimeFormatData: async (
    clientId: string,
    taxYear: number
  ): Promise<any | IServerError | IResponse | null> => {
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/salary-profile/salary-profile-edr?client_id=${clientId}`
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
};
